<template>
  <div>
    <cl-button
      variant="secondary"
      type="button"
      data-test-id="dashboard-data-table-export"
      @on-click="exportSelectedAccounts"
    >
      {{ t("export") }}
      <span v-if="props.withExportCount">{{
        `(${accountsToExport.length})`
      }}</span>
    </cl-button>
  </div>
</template>

<script setup lang="ts">
import { toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { Customer } from "../types/index.ts";
import { $downloadCSV } from "@/plugins/jsonToCsv";
import dayjs from "dayjs";
import { ALL_SERVICES } from "@/utils/services.ts";
import { toKebabCase } from "@/modules/Usage/views/Msp/Overview/CustomersList/utils/transformOrganisationsToCustomers.ts";
import { useToastStore } from "@/components/TToasts/toast.store.ts";

const toastStore = useToastStore();

const { t } = useI18n();

interface Props {
  accountsToExport: Customer[];
  headers: { id: string }[];
  selectedServiceUuid: string;
  withExportCount?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  withExportCount: false,
});
const { accountsToExport } = toRefs(props);

const exportSelectedAccounts = () => {
  try {
    const exportableHeaders = props.headers
      .filter(({ id }) => id !== "select" && id !== "actions")
      .map(({ id }) => ({
        key: id,
        label: formatHeaderTitle(id),
      }));

    const extractValue = (res: Customer, key: string): string | number => {
      const record = res as Record<string, unknown>;
      const value = record[key];

      if (key === "created_at" && typeof value === "string") {
        return dayjs(value).format("YYYY-MM-DD H:mm:ss");
      }
      if (key === "seats_utilized_ratio" && typeof value === "number") {
        const percentage = Math.round(value * 100) || 0;
        return `${percentage}%`;
      }
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
      }
      if (typeof value === "string" || typeof value === "number") {
        return value;
      }
      return "";
    };

    const exportData = accountsToExport.value.map((res: Customer) =>
      exportableHeaders.reduce(
        (payload, { key, label }) => {
          payload[label] = extractValue(res, key);
          return payload;
        },
        {} as Record<string, string | number>,
      ),
    );

    let csvFileName = "licence-usage-customers";
    if (props.selectedServiceUuid) {
      const service = ALL_SERVICES.find(
        (service) => service.uuid === props.selectedServiceUuid,
      )!;
      const serviceName = toKebabCase(t(service.menuItemLabel));
      csvFileName = `${serviceName}-${csvFileName}`;
    }

    $downloadCSV(exportData, csvFileName);
  } catch (e) {
    toastStore.displayToast({
      title: t("error"),
      message: "",
      duration: 2000,
      variant: "danger",
      testId: "customer-export-toast-error",
    });

    /* eslint-disable no-console */
    console.error(e);
  }
};

const formatHeaderTitle = (field: string) => {
  if (field === "seats_utilized_ratio") {
    return t("total-usage");
  }
  if (field === "seats_used") {
    return t("active-users");
  }
  if (field === "seats_purchased") {
    return t("licenses-issued");
  }
  return field
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
</script>
